import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getFramework } from '@/framework';
import { CellsSelectedInfo, getTableEditState } from '@/plugins/table-edit/state';
import addOrUpdateLayer from '@/plugins/table-edit/utils/add-or-update-layer';
import { COL_ELEMENT_IS_BLOCK } from '@/utils/common-kit';
import { Cell, ITableLayer } from '@/version-types';
import { getUpdatedTableStyle, GetUpdatedTableStyle_Action } from '../utils/table';
import { getCellComponentHTML } from '../view/cell-component';

export const mergeCells = ({
    frameworkIdentity,
    tableLayer,
    targetCellIds,
}: {
    frameworkIdentity: string;
    tableLayer: ITableLayer;
    targetCellIds: string[];
}) => {
    const framework = getFramework(frameworkIdentity);
    const { setState } = getTableEditState(frameworkIdentity);
    let newTableLayer = cloneDeep(tableLayer);
    const { cells: cellsProp } = newTableLayer.data;
    const cells = cellsProp.flat().filter(Boolean) as Cell[];
    const targetCells = cells.filter((cell) => targetCellIds.includes(cell.__renderId));
    // 最左上角的cell, 其它cell都合并到这个里面
    const targetCell = (() => {
        const sortedTargetCells = targetCells.sort((a, b) => (a.hIndex <= b.hIndex && a.vIndex <= b.vIndex ? -1 : 1));
        return sortedTargetCells[0];
    })();
    const delCells = targetCells.filter((cell) => cell.__renderId !== targetCell.__renderId);
    // 最右下角的cell
    const lastCell = (() => {
        const sortedTargetCells = targetCells.sort((a, b) =>
            a.hIndex + a.colSpan <= b.hIndex + b.colSpan && a.vIndex + a.rowSpan <= b.vIndex + b.rowSpan ? -1 : 1,
        );
        return sortedTargetCells[sortedTargetCells.length - 1];
    })();

    const tempDomActions: GetUpdatedTableStyle_Action[] = [];

    // 1. 文字合并
    const newContent =
        targetCell.content + delCells.reduce((res, cell) => res + (cell.content.length ? '\n' + cell.content : ''), '');
    targetCell.content = newContent;
    tempDomActions.push((_, { updateTextContentOrAttr }) =>
        updateTextContentOrAttr({
            currentTableLayerData: tableLayer.data,
            fontFamilyMap: tableLayer.__runtime?.fontFamilyMap ?? {},
            cellInfo: {
                id: targetCell.__renderId,
                hIndex: targetCell.hIndex,
                vIndex: targetCell.vIndex,
            },
            newContent,
        }),
    );

    // 2. targetCell尺寸修改
    targetCell.colSpan = lastCell.hIndex + lastCell.colSpan - targetCell.hIndex;
    targetCell.rowSpan = lastCell.vIndex + lastCell.rowSpan - targetCell.vIndex;
    tempDomActions.push((_, { getTdEl }) => {
        const tdEl = getTdEl(targetCell.__renderId)!;
        tdEl.colSpan = targetCell.colSpan;
        tdEl.rowSpan = targetCell.rowSpan;
    });

    // 3. 删除其它cells
    delCells.forEach((c) => {
        newTableLayer.data.cells[c.vIndex][c.hIndex] = null;
    });
    tempDomActions.push((_, { getTdEl }) => {
        delCells.forEach((c) => {
            const tdEl = getTdEl(c.__renderId)!;
            tdEl.remove();
        });
    });

    // 4. 更新layout
    const newInfo = getUpdatedTableStyle({
        frameworkIdentity,
        tableLayerId: newTableLayer.id,
        tableRotate: newTableLayer.rotate,
        action: (...args) => {
            tempDomActions.forEach((action) => action(...args));
        },
        compute: (_, { getLayout }) =>
            getLayout({
                tableWidth: newTableLayer.width,
                tableHeight: newTableLayer.height,
                horizontalSizes: newTableLayer.data.baseCells.horizontal,
                verticalSizes: newTableLayer.data.baseCells.vertical,
            }),
    })!;
    if (COL_ELEMENT_IS_BLOCK) {
        newTableLayer.width = newInfo.tableWidth;
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells = newInfo.baseCells;
    } else {
        // 降级处理
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
    }

    newTableLayer = addOrUpdateLayer({
        frameworkIdentity,
        layer: newTableLayer,
        resetMaterialIds: true,
    });
    framework.operate.setActiveLayer(newTableLayer.id);
    framework.history.write(cloneDeep(tableLayer), newTableLayer);

    const newCellsSelectedInfo: CellsSelectedInfo = {
        rect: {
            startHIndex: targetCell.hIndex,
            startVIndex: targetCell.vIndex,
            endHIndex: targetCell.hIndex + targetCell.colSpan,
            endVIndex: targetCell.vIndex + targetCell.rowSpan,
        },
        cellInfoList: [
            {
                id: targetCell.__renderId,
                vIndex: targetCell.vIndex,
                hIndex: targetCell.hIndex,
            },
        ],
    };
    setState({
        cellsSelectedInfo: newCellsSelectedInfo,
        rowOrColSelectedRectInfo: null,
    });
};

/** 拆分单元格 */
export const cancelMergeCells = ({
    frameworkIdentity,
    tableLayer,
    targetCellId,
}: {
    frameworkIdentity: string;
    tableLayer: ITableLayer;
    targetCellId: string;
}) => {
    const framework = getFramework(frameworkIdentity);
    const { setState } = getTableEditState(frameworkIdentity);
    const originalTargetCell = tableLayer.data.cells.flat().find((c) => c?.__renderId === targetCellId);
    if (!originalTargetCell) return;
    const isMergedCell = originalTargetCell.colSpan > 1 || originalTargetCell.rowSpan > 1;
    if (!isMergedCell) return;

    let newTableLayer = cloneDeep(tableLayer);
    const targetCell = newTableLayer.data.cells.flat().find((c) => c?.__renderId === targetCellId)!;

    const newSelectedCellInfoList: { id: string; hIndex: number; vIndex: number }[] = [
        {
            id: targetCell.__renderId,
            hIndex: targetCell.hIndex,
            vIndex: targetCell.vIndex,
        },
    ];
    const tempDomActions: GetUpdatedTableStyle_Action[] = [];

    // 1. targetCell变为最左上角的cell
    targetCell.colSpan = 1;
    targetCell.rowSpan = 1;
    tempDomActions.push((el, { getTdEl }) => {
        const tdEl = getTdEl(targetCell.__renderId)!;
        tdEl.colSpan = 1;
        tdEl.rowSpan = 1;
    });

    // 2. 补充其它cell
    for (
        let hIndex = originalTargetCell.hIndex;
        hIndex < originalTargetCell.hIndex + originalTargetCell.colSpan;
        hIndex++
    ) {
        for (
            let vIndex = originalTargetCell.vIndex;
            vIndex < originalTargetCell.vIndex + originalTargetCell.rowSpan;
            vIndex++
        ) {
            if (hIndex === originalTargetCell.hIndex && vIndex === originalTargetCell.vIndex) continue;

            const newCellId = uuidv4();

            const newCell = cloneDeep(originalTargetCell);
            newCell.__renderId = newCellId;
            newCell.hIndex = hIndex;
            newCell.vIndex = vIndex;
            newCell.colSpan = 1;
            newCell.rowSpan = 1;
            newCell.content = '';
            newTableLayer.data.cells[vIndex][hIndex] = newCell;

            tempDomActions.push((el) => {
                const targetEl = el.rows[vIndex]?.querySelectorAll('td')[hIndex];
                if (targetEl)
                    targetEl.outerHTML = getCellComponentHTML({
                        cellComponentProps: {
                            cell: newCell,
                            tableLayerData: newTableLayer.data,
                            fontFamilyMap: newTableLayer.__runtime?.fontFamilyMap ?? {},
                            isInEditorLayer: true,
                        },
                    });
            });

            newSelectedCellInfoList.push({
                id: newCell.__renderId,
                hIndex: newCell.hIndex,
                vIndex: newCell.vIndex,
            });
        }
    }

    const newInfo = getUpdatedTableStyle({
        frameworkIdentity,
        tableLayerId: newTableLayer.id,
        tableRotate: newTableLayer.rotate,
        action: (...args) => {
            tempDomActions.forEach((action) => action(...args));
        },
        compute: (_, { getLayout }) =>
            getLayout({
                tableWidth: newTableLayer.width,
                tableHeight: newTableLayer.height,
                horizontalSizes: newTableLayer.data.baseCells.horizontal,
                verticalSizes: newTableLayer.data.baseCells.vertical,
            }),
    })!;
    if (COL_ELEMENT_IS_BLOCK) {
        newTableLayer.width = newInfo.tableWidth;
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells = newInfo.baseCells;
    } else {
        // 降级处理
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
    }

    newTableLayer = addOrUpdateLayer({
        frameworkIdentity,
        layer: newTableLayer,
        resetMaterialIds: false,
    });
    framework.operate.setActiveLayer(newTableLayer.id);
    framework.history.write(cloneDeep(tableLayer), newTableLayer);

    const newCellsSelectedInfo: CellsSelectedInfo = {
        cellInfoList: newSelectedCellInfoList,
        rect: {
            startHIndex: targetCell.hIndex,
            startVIndex: targetCell.vIndex,
            endHIndex: originalTargetCell.hIndex + originalTargetCell.colSpan,
            endVIndex: originalTargetCell.vIndex + originalTargetCell.rowSpan,
        },
    };
    setState({
        cellsSelectedInfo: newCellsSelectedInfo,
        rowOrColSelectedRectInfo: null,
    });
};
